import { Avatar, Badge, Group, Paper, Stack, Tabs, Text, ThemeIcon, Title } from "@mantine/core";
import { IconShieldCheck, IconUserCircle } from "@tabler/icons-react";
import type { ReactNode } from "react";
import { axioTokens } from "../../theme";

export const PROFILE_TAB_TIMELINE = "Timeline";
export const PROFILE_TAB_RELATED = "Related";
export const PROFILE_TAB_DETAILS = "Details";
export const PROFILE_TAB_DOCUMENTS = "Documents";

interface ProfileShellProps {
  name: string;
  subtitle: ReactNode;
  role: string;
  status: string;
  stats?: ReactNode;
  beforeTabs?: ReactNode;
  timeline: ReactNode;
  details: ReactNode;
  related?: ReactNode;
  documents?: ReactNode;
  actions?: ReactNode;
  showSubtitleIcon?: boolean;
}

export function ProfileShell({
  name,
  subtitle,
  role,
  status,
  stats,
  beforeTabs,
  timeline,
  details,
  related,
  documents,
  actions,
  showSubtitleIcon = true,
}: ProfileShellProps) {
  const statusActive =
    status.toLowerCase().includes("active") ||
    status.toLowerCase().includes("consented") ||
    status.toLowerCase().includes("verified");

  return (
    <Stack gap="var(--axio-unit-32)">
      <Paper
        withBorder
        p="xl"

        style={{
          background: axioTokens.surfaceElevated,
          borderColor: axioTokens.border,
          boxShadow: "none",
        }}
      >
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
          <Group gap="lg">
            <Avatar
              color="gray"
              radius={axioTokens.radiusCircle}
              size="var(--axio-unit-72)"
              style={{ boxShadow: axioTokens.shadow.avatarRing }}
            >
              {name[0]?.toUpperCase() ?? "U"}
            </Avatar>
            <div>
              <Title order={2}>{name}</Title>
              <Group gap="xs" mt="var(--axio-unit-6)" align="flex-start">
                {showSubtitleIcon ? (
                  <ThemeIcon variant="light" color="gray" size="var(--axio-unit-24)">
                    <IconUserCircle size="var(--axio-unit-14)" />
                  </ThemeIcon>
                ) : null}
                {typeof subtitle === "string" ? <Text c="dimmed">{subtitle}</Text> : subtitle}
              </Group>
            </div>
          </Group>
          <Group>
            <Badge color="gray" variant="outline" size="lg">
              {role}
            </Badge>
            <Badge
              variant="outline"
              color={statusActive ? "green" : "gray"}
              leftSection={<IconShieldCheck size="var(--axio-unit-12)" />}
              size="lg"

            >
              {status}
            </Badge>
            {actions}
          </Group>
        </Group>
      </Paper>
      {stats}
      {beforeTabs}
      <Tabs defaultValue="timeline" variant="pills">
        <Tabs.List>
          <Tabs.Tab value="timeline">{PROFILE_TAB_TIMELINE}</Tabs.Tab>
          {related ? <Tabs.Tab value="related">{PROFILE_TAB_RELATED}</Tabs.Tab> : null}
          <Tabs.Tab value="details">{PROFILE_TAB_DETAILS}</Tabs.Tab>
          {documents ? <Tabs.Tab value="documents">{PROFILE_TAB_DOCUMENTS}</Tabs.Tab> : null}
        </Tabs.List>
        <Tabs.Panel value="timeline" pt="md">
          {timeline}
        </Tabs.Panel>
        {related ? (
          <Tabs.Panel value="related" pt="md">
            {related}
          </Tabs.Panel>
        ) : null}
        <Tabs.Panel value="details" pt="md">
          {details}
        </Tabs.Panel>
        {documents ? (
          <Tabs.Panel value="documents" pt="md">
            {documents}
          </Tabs.Panel>
        ) : null}
      </Tabs>
    </Stack>
  );
}
